import React from "react";
import { IconButton } from "@fluentui/react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Divider from "@mui/material/Divider";
import {
  getDisplayFileName,
  getTitle,
  isWorkingDocumentCheckedOut,
  getWorkingDocumentEditedLabel,
  isDocumentReleased,
} from "../../../../functions/helperMethod";
import {
  cardSx,
  cardHeaderSx,
  titleTypography,
  valueTypography,
  dividerSx,
} from "../../../../components/oneDriveDocuments/WorkingDocuments.styles";
import wordIcon from "../../../../assets/workingDocIcons/wordIcon.png";
import excelIcon from "../../../../assets/workingDocIcons/excelIcon.png";
import pdfIcon from "../../../../assets/workingDocIcons/pdfIcon.png";
import pptIcon from "../../../../assets/workingDocIcons/pptIcon.png";
import wordIconAvatar from "../../../../assets/workingDocIcons/wordIconAvatar.png";
import excelIconAvatar from "../../../../assets/workingDocIcons/excelIconAvatar.png";
import pdfIconAvatar from "../../../../assets/workingDocIcons/pdfIconAvatar.png";
import pptIconAvatar from "../../../../assets/workingDocIcons/pptIconAvatar.png";
import outlookIconAvatar from "../../../../assets/workingDocIcons/outlookIconAvatar.png";
import imageIconAvatar from "../../../../assets/workingDocIcons/imageIconAvatar.png";
import audioIconAvatar from "../../../../assets/workingDocIcons/audioIconAvatar.png";
import videoIconAvatar from "../../../../assets/workingDocIcons/vedioIconAvatar.png";
import archiveIconAvatar from "../../../../assets/workingDocIcons/archiveAvatar.png";
import textIconAvatar from "../../../../assets/workingDocIcons/textIconAvatar.png";
import otherFileIconAvatar from "../../../../assets/workingDocIcons/otherFile.png";
import { OAS365ICONS } from "../../../../icons";

const getMenuItemSx = (theme) => ({
  fontSize: `${theme.contextMenu?.fontSize ?? 12}px`,
  fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts?.regular,
  paddingTop: "4px",
  paddingBottom: "4px",
  minHeight: "auto",
  lineHeight: 1.5,
  "&:hover": {
    backgroundColor: theme.colors?.contextMenuHover || "#E2EFDF",
    color: theme.colors?.themeFontColor || "inherit",
  },
  "&:focus-visible": {
    outline: "none",
  },
});

function getInitials(name) {
  if (!name || name === "Unknown User") return "UU";
  const nameParts = name.split(" ");
  if (nameParts.length === 1) {
    return name.substring(0, 2).toUpperCase();
  }
  return (
    nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)
  ).toUpperCase();
}

function getRelativeTime(dateString) {
  if (!dateString) return "-";
  const [datePart, timePart] = dateString.split(" ");
  const [month, day, year] = datePart.split("/").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);
  const utcDate = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second)
  );
  const localDate = new Date(utcDate);
  const now = new Date();
  const diffInSeconds = Math.floor((now - localDate) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInSeconds < 60) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const weekdayFormatter = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (diffInDays < 7) return weekdayFormatter.format(localDate);
  if (localDate.getFullYear() === now.getFullYear()) {
    return monthDayFormatter.format(localDate);
  }
  return fullDateFormatter.format(localDate);
}

export function getDocIcon(name, isCheckedOut = false, type = "default", isReleased = false) {
  if (!name) {
    if (isReleased) {
      return type === "avatar" ? wordIconAvatar : OAS365ICONS.wordReleasedIcon;
    }
      if (isCheckedOut) {
        return type === "avatar" ? wordIconAvatar : OAS365ICONS.wordLockIcon;
      }
    return type === "avatar" ? wordIconAvatar : wordIcon;
  }

  const ext = name.split(".").pop().toLowerCase();

  // File type groups (keep in sync with WorkingDocuments.js)
  const emailExts = ["msg", "eml"];
  const wordExts = ["doc", "docx", "docm", "dot", "dotm", "dotx", "rtf", "odt"];
  const excelExts = ["xls", "xlsx", "xlsm", "csv", "xlsb", "ods", "xlt", "xltx", "xltm"];
  const pptExts = [
    "ppt",
    "pptx",
    "pptm",
    "potm",
    "potx",
    "pps",
    "ppsx",
    "ppsm",
    "pot",
  ];
  const pdfExts = ["pdf"];
  const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff", "webp", "svg"];
  const audioExts = ["mp3", "wav", "aac", "ogg", "flac", "m4a", "wma", "opus"];
  const videoExts = [
    "mp4",
    "avi",
    "mov",
    "wmv",
    "flv",
    "webm",
    "mkv",
    "mpeg",
    "mpg",
    "3gp",
    "ogv",
  ];
  const archiveExts = ["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "cab", "iso"];
  const textExts = ["txt", "html", "json", "xml", "css", "js", "javascript"];

  if (isReleased) {
    if (emailExts.includes(ext)) {
      return type === "avatar" ? outlookIconAvatar : OAS365ICONS.outlookReleasedIcon;
    }
    if (wordExts.includes(ext)) {
      return type === "avatar" ? wordIconAvatar : OAS365ICONS.wordReleasedIcon;
    }
    if (excelExts.includes(ext)) {
      return type === "avatar" ? excelIconAvatar : OAS365ICONS.excelReleasedIcon;
    }
    if (pptExts.includes(ext)) {
      return type === "avatar" ? pptIconAvatar : OAS365ICONS.powerpointReleasedIcon;
    }
    if (pdfExts.includes(ext)) {
      return type === "avatar" ? pdfIconAvatar : OAS365ICONS.pdfReleasedIcon;
    }
    if (imageExts.includes(ext)) {
      return type === "avatar" ? imageIconAvatar : OAS365ICONS.imageReleasedIcon;
    }
    if (audioExts.includes(ext)) {
      return type === "avatar" ? audioIconAvatar : OAS365ICONS.audioReleasedIcon;
    }
    if (videoExts.includes(ext)) {
      return type === "avatar" ? videoIconAvatar : OAS365ICONS.videoReleasedIcon;
    }
    if (archiveExts.includes(ext)) {
      return type === "avatar" ? archiveIconAvatar : OAS365ICONS.archiveReleasedIcon;
    }
    if (textExts.includes(ext)) {
      return type === "avatar" ? textIconAvatar : OAS365ICONS.textReleasedIcon;
    }
    return type === "avatar" ? otherFileIconAvatar : OAS365ICONS.otherFileIcon;
  }

  // Checked-out icons
  if (isCheckedOut) {
    if (emailExts.includes(ext)) {
      return type === "avatar" ? outlookIconAvatar : OAS365ICONS.outlookLockIcon;
    }
      if (wordExts.includes(ext)) {
        return type === "avatar" ? wordIconAvatar : OAS365ICONS.wordLockIcon;
      }
      if (excelExts.includes(ext)) {
        return type === "avatar" ? excelIconAvatar : OAS365ICONS.excelLockIcon;
      }
      if (pptExts.includes(ext)) {
        return type === "avatar" ? pptIconAvatar : OAS365ICONS.powerpointLockIcon;
      }
      if (pdfExts.includes(ext)) {
        return type === "avatar" ? pdfIconAvatar : OAS365ICONS.pdfLockIcon;
      }

    if (imageExts.includes(ext)) {
      return type === "avatar" ? imageIconAvatar : OAS365ICONS.imageLockIcon;
    }
    if (audioExts.includes(ext)) {
      return type === "avatar" ? audioIconAvatar : OAS365ICONS.audioLockIcon;
    }
    if (videoExts.includes(ext)) {
      return type === "avatar" ? videoIconAvatar : OAS365ICONS.videoLockIcon;
    }
    if (archiveExts.includes(ext)) {
      return type === "avatar" ? archiveIconAvatar : OAS365ICONS.archiveLockIcon;
    }
    if (textExts.includes(ext)) {
      return type === "avatar" ? textIconAvatar : OAS365ICONS.textLockIcon;
    }

    return type === "avatar" ? otherFileIconAvatar : OAS365ICONS.otherFileIcon;
  }

  // Normal (not checked out) icons
  if (emailExts.includes(ext)) {
    return type === "avatar" ? outlookIconAvatar : OAS365ICONS.outlook;
  }
  if (wordExts.includes(ext)) {
    return type === "avatar" ? wordIconAvatar : wordIcon;
  }
  if (excelExts.includes(ext)) {
    return type === "avatar" ? excelIconAvatar : excelIcon;
  }
  if (pptExts.includes(ext)) {
    return type === "avatar" ? pptIconAvatar : pptIcon;
  }
  if (pdfExts.includes(ext)) {
    return type === "avatar" ? pdfIconAvatar : pdfIcon;
  }

  if (imageExts.includes(ext)) {
    return type === "avatar" ? imageIconAvatar : OAS365ICONS.imageIcon;
  }
  if (audioExts.includes(ext)) {
    return type === "avatar" ? audioIconAvatar : OAS365ICONS.audioFileIcon;
  }
  if (videoExts.includes(ext)) {
    return type === "avatar" ? videoIconAvatar : OAS365ICONS.videoFileIcon;
  }
  if (archiveExts.includes(ext)) {
    return type === "avatar" ? archiveIconAvatar : OAS365ICONS.archive;
  }
  if (textExts.includes(ext)) {
    return type === "avatar" ? textIconAvatar : OAS365ICONS.textIcon;
  }

  return type === "avatar" ? otherFileIconAvatar : OAS365ICONS.otherFileIcon;
}

function getLastModifiedLabel(relativeTime) {
  if (relativeTime === "Just now" || relativeTime.includes("m ago")) {
    return "Last modified";
  }
  if (relativeTime.includes("h ago")) {
    const hours = parseInt(relativeTime, 10);
    return hours >= 24 ? "Last modified on" : "Last modified";
  }
  return "Last modified on";
}

function getLastActionUser(file) {
  const lastAction = file.LastAction || file.lastAction;
  if (!lastAction) return null;
  const idx = lastAction.lastIndexOf(" by ");
  if (idx === -1) return null;
  return lastAction.substring(idx + 4).trim() || null;
}

function getModifiedDate(dateString) {
  if (!dateString) return "-";
  const dt = new Date(dateString);
  if (Number.isNaN(dt.getTime())) return "-";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatCodeWithName(code, name) {
  const codeText = (code ?? "").toString().trim();
  const nameText = (name ?? "").toString().trim();
  if (codeText && nameText) return `${codeText} - ${nameText}`;
  return codeText || nameText;
}

function getWorkingDocState(file) {
  const raw = file?.MetaData ?? file?.metaData;
  if (raw && typeof raw === "object") return raw.State ?? raw.state ?? "";
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed?.State ?? parsed?.state ?? "";
    } catch {
      return "";
    }
  }
  return "";
}


function getWorkingDocTooltipContent(file, displayTitle) {
  const docNo = file?.docNo ?? file?.DocNo ?? "";
  const docRev = file?.docRev ?? file?.DocRev ?? "";
  const classText = formatCodeWithName(
    file?.DocClass ?? file?.docClass,
    file?.DocClassName ?? file?.docClassName,
  );
  const formatText = formatCodeWithName(
    file?.FormatSize ?? file?.formatSize,
    file?.FormatSizeName ?? file?.formatSizeName,
  );
  const state = getWorkingDocState(file);
  const modified = file?.LastAction ?? file?.lastAction ?? "";
  return [
    displayTitle ? `Title: ${displayTitle}` : "",
    docNo || docRev ? `Document No: ${docNo} - ${docRev}` : "",
    classText ? `Class: ${classText}` : "",
    formatText ? `Format: ${formatText}` : "",
    state ? `State: ${state}` : "",
    modified ? `Modified: ${modified}` : "",
    getWorkingDocumentEditedLabel(file),
  ].filter(Boolean);
}

function WorkingDocumentCard({
  file,
  theme,
  anchorEl,
  selectedDocId,
  selectedDocument,
  onOpenDetails,
  onMenuOpen,
  onMenuClose,
  onMenuClick,
  onOpenDoc,
  onOpenInIFS,
  onSetDocumentToDeleteOneDrive,
  onAllowSelectionChange,
  onDeleteFromOneDrive,
  isDeleteTarget = false,
  isDimmed = false,
  viewMode = "card",
}) {
  const displayName =
    file.lastModifiedBy?.user?.displayName || getLastActionUser(file) || "Unknown User";
  const relativeTime = getRelativeTime(file.lastModifiedDateTime);
  const isCheckedOut = isWorkingDocumentCheckedOut(file);
  const isReleased = isDocumentReleased(file);
  const displayTitle = getTitle(getDisplayFileName(file.name));
  const fileIconSrc = getDocIcon(file.name, isCheckedOut, "default", isReleased);
  const modifiedDate = getModifiedDate(file.lastModifiedDateTime);
  const docNo = file.docNo ?? file.DocNo ?? "-";
  const docRev = file.docRev ?? file.DocRev ?? "-";
  const docClass =
    file.docClassDescription ??
    file.DocClassDescription ??
    file.DocClassName ??
    file.docClassName ??
    file.docClass ??
    file.DocClass ??
    "-";

  const iconTooltipContent = getWorkingDocTooltipContent(file, displayTitle);
  const iconTooltipTitle =
    iconTooltipContent.length > 0 ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {iconTooltipContent.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </Box>
    ) : (
      ""
    );
  const iconTooltipSlotProps = {
    tooltip: {
      sx: {
        backgroundColor: theme.colors?.themeDarkColor ?? "#1f7a45",
        color: theme.colors?.addovationWhite ?? "#ffffff",
        fontFamily: theme.fonts?.regular,
        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
        lineHeight: 1.4,
        maxWidth: 260,
        padding: "8px 12px",
        borderRadius: theme.border?.radius?.textField ?? "6px",
        "& .MuiTooltip-arrow": {
          color: theme.colors?.themeDarkColor ?? "#1f7a45",
        },
      },
    },
  };

  if (viewMode === "list") {
    const listView = (
      <Box
        aria-label={displayTitle}
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(340px,1fr) 110px 95px 70px minmax(150px,1fr) 40px",
          alignItems: "center",
          columnGap: "12px",
          padding: "10px 12px",
          margin: "0 10px",
          borderBottom: `1px solid ${theme.colors.Addovation20Grey ?? "#E6E6E6"}`,
          background: theme.colors.backgroundColor,
          opacity: isDimmed ? 0.4 : 1,
          transition: "background-color 0.2s ease, opacity 0.2s ease",
          ...(isDeleteTarget && {
            backgroundColor: "rgba(217,45,32,0.05)",
            borderBottom: "1px solid #D92D20",
          }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1 }}>
          <Tooltip
            title={iconTooltipTitle}
            placement="right"
            arrow
            slotProps={iconTooltipSlotProps}
          >
            <img
              alt={file.Title || file.name}
              src={fileIconSrc}
              style={{ width: "20px", height: "20px", objectFit: "contain", flexShrink: 0 }}
            />
          </Tooltip>
          <Typography
            component="div"
            title={displayTitle || ""}
            sx={{
              ...titleTypography(theme),
              fontSize: "13px",
              fontWeight: 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayTitle}
          </Typography>
        </Box>
        <Typography sx={{ ...valueTypography(theme), fontSize: "13px", fontWeight: 400 }}>
          {modifiedDate}
        </Typography>
        <Typography sx={{ ...valueTypography(theme), fontSize: "13px", fontWeight: 400 }}>
          {docNo}
        </Typography>
        <Typography sx={{ ...valueTypography(theme), fontSize: "13px", fontWeight: 400 }}>
          {docRev}
        </Typography>
        <Typography
          title={docClass}
          sx={{
            ...valueTypography(theme),
            fontSize: "13px",
            fontWeight: 400,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {docClass}
        </Typography>
        <IconButton
          aria-label="settings"
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            onMenuOpen(ev, file.id, file);
          }}
          sx={{
            color: theme.colors.fontColor,
            "&:hover": {
              color: theme.colors.themeFontColor,
              backgroundColor: theme.colors.contextMenuHover,
            },
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && selectedDocId === file.id}
          onClose={onMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: theme.colors.pureColor,
              color: theme.colors.fontColor,
              fontSize: `${theme.contextMenu?.fontSize ?? 12}px`,
              fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts?.regular,
            },
          }}
        >
          <MenuItem
            onClick={(ev) =>
              void onMenuClick(ev, "WorkingDocumentUnifiedCheckIn", file)
            }
            sx={getMenuItemSx(theme)}
          >
            Check-in
          </MenuItem>
          <MenuItem
            onClick={(ev) => {
              ev.stopPropagation();
              onMenuClose(ev);
              onOpenInIFS(file);
            }}
            sx={getMenuItemSx(theme)}
          >
            Open in IFS Cloud
          </MenuItem>
          <MenuItem
            sx={getMenuItemSx(theme)}
            onClick={(ev) => {
              ev.stopPropagation();
              onMenuClose(ev);
                if (file.webUrl || file.WebUrl) onOpenDoc(file.webUrl ?? file.WebUrl);
            }}
          >
            Edit in Browser
          </MenuItem>
          <MenuItem
            onClick={(ev) => onMenuClick(ev, "Preview", file)}
            sx={getMenuItemSx(theme)}
          >
            Preview in Teams
          </MenuItem>
          {typeof onDeleteFromOneDrive === "function" ? (
            <MenuItem
              onClick={(ev) => onDeleteFromOneDrive(ev, file)}
              sx={getMenuItemSx(theme)}
            >
              Delete from OneDrive
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              onMenuClose(ev);
              setTimeout(() => onOpenDetails(ev, file), 0);
            }}
            sx={getMenuItemSx(theme)}
          >
            Details
          </MenuItem>
        </Menu>
      </Box>
    );
    return listView;
  }

  const cardView = (
    <Card
      key={file.id || file.name}
      aria-label={getTitle(file.name)}
      sx={{
        ...cardSx,
        color: theme.colors.fontColor,
        background: theme.colors.contextMenuBackground,
        height: "175px",
        minHeight: "175px",
        maxHeight: "175px",
        overflow: "hidden",
        margin: "10px",
        display: "flex",
        flexDirection: "column",
        padding: "0px",
        justifyContent: "space-between",
        position: "relative",
        opacity: isDimmed ? 0.4 : 1,
        transition:
          "box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
        border: isDeleteTarget ? "1px solid #D92D20" : "1px solid transparent",
        boxShadow: isDeleteTarget
          ? "0 0 0 1px rgba(217,45,32,0.28), 0 0 16px rgba(217,45,32,0.45)"
          : undefined,
        ":hover": isDeleteTarget
          ? {
            boxShadow:
              "0 0 0 1px rgba(217,45,32,0.35), 0 0 18px rgba(217,45,32,0.55)",
          }
          : {
            boxShadow: `0px 4px 20px ${theme.colors.themeColor50}`,
          },
      }}
    >
      {/* Menu button */}
      <Box
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          zIndex: 2,
        }}
      >
        <IconButton
          aria-label="settings"
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            onMenuOpen(ev, file.id, file);
          }}
          sx={{
            color: theme.colors.fontColor,
            "&:hover": {
              color: theme.colors.themeFontColor,
              backgroundColor: theme.colors.contextMenuHover,
            },
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && selectedDocId === file.id}
          onClose={onMenuClose}
          disableScrollLock
          PaperProps={{
            sx: {
              backgroundColor: theme.colors.pureColor,
              color: theme.colors.fontColor,
              fontSize: `${theme.contextMenu?.fontSize ?? 12}px`,
              fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts?.regular,
            },
          }}
        >
          <MenuItem
            onClick={(ev) =>
              void onMenuClick(ev, "WorkingDocumentUnifiedCheckIn", file)
            }
            sx={getMenuItemSx(theme)}
          >
            Check-in
          </MenuItem>
          <MenuItem
            onClick={(ev) => {
              ev.stopPropagation();
              onMenuClose(ev);
              onOpenInIFS(file);
            }}
            sx={getMenuItemSx(theme)}
          >
            Open in IFS Cloud
          </MenuItem>
          <MenuItem
            sx={getMenuItemSx(theme)}
            onClick={(ev) => {
              ev.stopPropagation();
              onMenuClose(ev);
                if (file.webUrl || file.WebUrl) onOpenDoc(file.webUrl ?? file.WebUrl);
            }}
          >
            Edit in Browser
          </MenuItem>
          <MenuItem
            onClick={(ev) => onMenuClick(ev, "Preview", file)}
            sx={getMenuItemSx(theme)}
          >
            Preview in Teams
          </MenuItem>
          {typeof onDeleteFromOneDrive === "function" ? (
            <MenuItem
              onClick={(ev) => onDeleteFromOneDrive(ev, file)}
              sx={getMenuItemSx(theme)}
            >
              Delete from OneDrive
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              onMenuClose(ev);
              setTimeout(() => onOpenDetails(ev, file), 0);
            }}
            sx={getMenuItemSx(theme)}
          >
            Details
          </MenuItem>
        </Menu>
      </Box>

      {/* Card content (non-clickable; clicks go to card) */}
      <Stack
        sx={{
          padding: "8px",
          height: "100%",
          position: "relative",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <CardHeader
          sx={{
            ...cardHeaderSx,
            paddingTop: "6px",
            paddingBottom: "12px",
          }}
          title={
            <Typography
              component="span"
              title={getTitle(getDisplayFileName(file.name)) || ""}
              sx={{
                ...titleTypography(theme),
                pointerEvents: "auto",
              }}
            >
              {getTitle(getDisplayFileName(file.name))}
            </Typography>
          }
        />
        <Divider variant="middle" sx={dividerSx(theme)} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexGrow: 1,
            padding: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 1,
              flex: 1,
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "12px",
                backgroundColor: "#71ad8d",
                marginBottom: "2px",
              }}
            >
              {getInitials(displayName)}
            </Avatar>
            <Box sx={{ minWidth: "150px", flexGrow: 1 }}>
              <Tooltip title={displayName} placement="top" arrow>
                <Typography
                  sx={{
                    ...valueTypography(theme),
                    fontSize: "12px",
                    fontWeight: "bold",
                    lineHeight: 1.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "150px",
                    display: "block",
                    pointerEvents: "auto",
                  }}
                >
                  {displayName}
                </Typography>
              </Tooltip>
              <Typography
                sx={{
                  ...valueTypography(theme),
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  fontFamily: theme.fonts?.regular,
                  color: theme.colors.subTitle,
                  lineHeight: 1.5,
                }}
              >
                {getLastModifiedLabel(relativeTime)}
              </Typography>
              <Typography
                sx={{
                  ...valueTypography(theme),
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  fontFamily: theme.fonts?.regular,
                  color: theme.colors.subTitle,
                  lineHeight: 1.5,
                }}
              >
                {relativeTime}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Tooltip
              title={iconTooltipTitle}
              placement="left"
              arrow
              slotProps={iconTooltipSlotProps}
            >
              <img
                alt={file.Title || file.name}
                src={fileIconSrc}
                style={{ width: "50px", pointerEvents: "auto" }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Stack>
    </Card>
  );

  return cardView;
}

export default WorkingDocumentCard;
